// PublicLayout — shared shell for the unauthenticated marketing site.
// Phase 16 Gate 4: dark mission-console aesthetic preserved.

import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import "../../styles/marketing.css";

const NAV_ITEMS = [
    { to: "/features", label: "Features" },
    { to: "/use-cases", label: "Use cases" },
    { to: "/evidence", label: "Evidence" },
    { to: "/demo", label: "Demo" },
    { to: "/security", label: "Security" },
    { to: "/docs", label: "Docs" },
    { to: "/contact", label: "Contact" },
];

export default function PublicLayout() {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const location = useLocation();

    // Close drawer when route changes.
    useEffect(() => {
        setDrawerOpen(false);
    }, [location.pathname]);

    // Cheap scroll-reveal: on every route change, watch elements with
    // .scroll-reveal / .cap-card and toggle .is-visible when they enter
    // the viewport.
    useEffect(() => {
        const els = document.querySelectorAll(".scroll-reveal, .cap-card");
        if (!("IntersectionObserver" in window)) {
            els.forEach((el) => el.classList.add("is-visible"));
            return;
        }
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e, idx) => {
                    if (e.isIntersecting) {
                        const target = e.target;
                        const delay = parseInt(target.getAttribute("data-delay") || "0", 10);
                        setTimeout(() => target.classList.add("is-visible"), delay);
                        obs.unobserve(target);
                    }
                });
            },
            { threshold: 0.12 },
        );
        els.forEach((el) => obs.observe(el));
        return () => obs.disconnect();
    }, [location.pathname]);

    return (
        <div className="public-shell" data-testid="public-shell">
            <header className="public-nav">
                <div className="public-nav__inner">
                    <Link to="/" className="public-brand" data-testid="public-brand-link">
                        <span className="public-brand__mark" />
                        <span className="public-brand__name">UAVSimNet</span>
                    </Link>
                    <nav className="public-nav__links" aria-label="Main navigation">
                        {NAV_ITEMS.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={false}
                                data-testid={`public-nav-link-${item.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    "public-nav__link" +
                                    (isActive ? " public-nav__link--active" : "")
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>
                    <Link to="/login" className="public-nav__cta" data-testid="public-nav-login">
                        Log In
                    </Link>
                    <button
                        type="button"
                        className="public-nav__menu-button"
                        onClick={() => setDrawerOpen((v) => !v)}
                        aria-label="Toggle menu"
                        data-testid="public-nav-menu-button"
                    >
                        {drawerOpen ? <X size={18} /> : <Menu size={18} />}
                    </button>
                </div>
                <div className={"public-nav__drawer" + (drawerOpen ? " open" : "")}
                     data-testid="public-nav-drawer">
                    {NAV_ITEMS.map((item) => (
                        <NavLink
                            key={`drawer-${item.to}`}
                            to={item.to}
                            data-testid={`public-drawer-link-${item.label.toLowerCase()}`}
                            className="public-nav__link"
                        >
                            {item.label}
                        </NavLink>
                    ))}
                    <NavLink to="/login" className="public-nav__link" data-testid="public-drawer-login">
                        Log In
                    </NavLink>
                </div>
            </header>
            <main style={{ flex: 1 }}>
                <Outlet />
            </main>
            <footer className="public-footer">
                <div className="public-footer__inner">
                    <div>
                        <div>
                            UAVSimNet — Counter-UAS Simulation Platform · © {new Date().getFullYear()}
                        </div>
                        {/*
                          Phase 10 — Cross-property authority link to the parent
                          organisation. We deliberately DO NOT use rel="nofollow"
                          because we want the authority signal to flow across
                          the two properties. `noopener` is retained as a
                          security hygiene default (no nofollow, no noreferrer —
                          we want the visit to be attributable).
                        */}
                        <div className="public-footer__rlk" data-testid="public-footer-rlk">
                            <a
                                href="https://therlkgroup.com"
                                target="_blank"
                                rel="noopener"
                                data-testid="public-footer-rlk-link"
                            >
                                Part of The RLK Group
                            </a>
                        </div>
                    </div>
                    <nav className="public-footer__links" aria-label="Footer">
                        <Link to="/about" className="public-footer__link" data-testid="public-footer-about">About</Link>
                        <Link to="/use-cases" className="public-footer__link" data-testid="public-footer-use-cases">Use cases</Link>
                        <Link to="/evidence" className="public-footer__link" data-testid="public-footer-evidence">Evidence</Link>
                        <Link to="/faq" className="public-footer__link" data-testid="public-footer-faq">FAQ</Link>
                        <Link to="/security" className="public-footer__link" data-testid="public-footer-security">Security</Link>
                        <Link to="/terms" className="public-footer__link" data-testid="public-footer-terms">Terms</Link>
                        <Link to="/privacy" className="public-footer__link" data-testid="public-footer-privacy">Privacy</Link>
                        <Link to="/aup" className="public-footer__link" data-testid="public-footer-aup">AUP</Link>
                        <Link to="/contact" className="public-footer__link" data-testid="public-footer-contact">Contact</Link>
                    </nav>
                </div>
            </footer>
        </div>
    );
}
